import { useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Inline AI smart-reply suggestions above the DM composer. Tap-to-generate (respects
// the shared daily AI quota). For Cipher Lock threads the caller passes transient
// plaintext in `messages` — nothing encrypted is ever sent to the server.
export function SmartReplies({ messages, onPick }) {
  const [state, setState] = useState("idle"); // idle | loading | done
  const [suggestions, setSuggestions] = useState([]);

  const generate = async () => {
    if (!messages?.length) return;
    setState("loading");
    try {
      const res = await api.smartReplies({ messages });
      const s = Array.isArray(res?.suggestions) ? res.suggestions.filter(Boolean) : [];
      setSuggestions(s);
      setState("done");
      if (!s.length) toast.info("No suggestions right now.");
    } catch (e) {
      setState("idle");
      const st = e?.response?.status;
      if (st === 429) toast.error("Daily AI limit reached — try again tomorrow.");
      else if (st === 400) toast.info("Nothing to reply to yet.");
      else toast.error("Couldn't generate replies.");
    }
  };

  if (state === "idle") {
    return (
      <div className="px-4 pt-2" data-testid="smart-replies">
        <button
          type="button"
          onClick={generate}
          className="flex items-center gap-1.5 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-3.5 py-1.5 text-xs font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15"
          data-testid="smart-replies-generate"
        >
          <Sparkles className="h-3.5 w-3.5" /> Suggest a reply
        </button>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div className="flex items-center gap-2 px-4 pt-2 text-xs text-bloq-muted" data-testid="smart-replies-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-bloq-mint" /> Generating replies…
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-2" data-testid="smart-replies-suggestions">
      {suggestions.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(s)}
          className="max-w-full truncate rounded-full border border-white/12 bg-bloq-s2 px-3.5 py-1.5 text-xs text-white transition-colors hover:border-bloq-mint/40 hover:bg-white/5"
          data-testid={`smart-reply-chip-${i}`}
          title={s}
        >
          {s}
        </button>
      ))}
      <button
        type="button"
        onClick={generate}
        className="flex h-7 w-7 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/8 hover:text-bloq-mint"
        data-testid="smart-replies-regenerate"
        title="Regenerate"
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
